(() => {
  'use strict';
  const section = document.querySelector('#build-your-ring');
  if (!section) return;
  const track = section.querySelector('.ring-builder-track');
  const cards = [...section.querySelectorAll('.ring-builder-card')];
  const prev = section.querySelector('[data-ring-prev]');
  const next = section.querySelector('[data-ring-next]');
  const controls = section.querySelector('.ring-builder-controls');
  const selector = section.querySelector('.ring-builder-color-selector');
  const choices = [...selector.querySelectorAll('input[type="radio"]')];
  const label = selector.querySelector('.ring-builder-metal-name');
  const status = selector.querySelector('.ring-builder-color-status');
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const previews = cards.map(card => ({
    card, image:card.querySelector('.ring-builder-image'),
    links:[...card.querySelectorAll('a')], fade:null, incoming:null, previous:null,
    hoverActive:false, hoverTimer:null, hoverRevision:0, displayed:null
  }));
  let current = choices.find(input => input.checked) || choices[0];
  let revision = 0;
  let pending = false;
  let userPaused = false;
  let visible = false;
  let hovering = false;
  let focused = false;
  let timer = null;
  const cycleMs = 6000;
  const mobile = window.matchMedia('(max-width: 850px)');
  let slideTimer = null;
  let slideIndex = 0;
  let dragging = false;
  const refreshSlides = () => {
    const running = mobile.matches && !dragging && !motion.matches && visible && !document.hidden;
    section.dataset.autoScrolling = String(running);
    if (!running) { clearTimeout(slideTimer); slideTimer = null; return; }
    if (slideTimer !== null) return;
    slideTimer = setTimeout(() => {
      slideTimer = null;
      slideIndex = (Math.round(track.scrollLeft / step()) + 1) % cards.length;
      track.scrollTo({left:slideIndex * step(), behavior:slideIndex === 0 ? 'auto' : 'smooth'});
      refreshSlides();
    }, 5000);
  };
  const decoded = new Map();
  const decode = src => {
    if (!decoded.has(src)) {
      const photo = new Image();
      photo.src = src;
      const ready = photo.decode().catch(error => { decoded.delete(src); throw error; });
      decoded.set(src, ready);
    }
    return decoded.get(src);
  };
  const clearFade = preview => {
    if (preview.fade) preview.fade.cancel();
    if (preview.incoming) preview.incoming.cancel();
    if (preview.previous) preview.previous.remove();
    preview.fade = null;
    preview.incoming = null;
    preview.previous = null;
  };
  const indicateColor = input => {
    choices.forEach(choice => { choice.checked = choice === input; });
    label.textContent = input.dataset.metalLabel;
  };
  const showColor = (preview, input, animate = true) => {
    const {card,image,links} = preview;
    clearFade(preview);
    const oldSource = image.currentSrc || image.src;
    image.src = card.dataset[input.value + 'Image'];
    image.alt = card.dataset.ringName + ' ring in ' + input.dataset.metalLabel;
    links.forEach(link => {
      link.href = card.dataset[input.value + 'DesignUrl'];
      link.setAttribute('aria-label', 'Build ' + card.dataset.ringName + ' in ' + input.dataset.metalLabel + ' (opens in a new tab)');
    });
    if (animate && !motion.matches && image.animate) {
      const old = document.createElement('img');
      old.className = 'ring-preview-previous';
      old.src = oldSource;
      old.alt = '';
      old.setAttribute('aria-hidden','true');
      const size = image.getBoundingClientRect();
      old.style.width = size.width + 'px';
      old.style.height = size.height + 'px';
      image.parentElement.appendChild(old);
      preview.previous = old;
      preview.fade = old.animate([{opacity:1},{opacity:0}], {duration:700,easing:'ease-in-out',fill:'forwards'});
      preview.incoming = image.animate([{opacity:0},{opacity:1}], {duration:700,easing:'ease-in-out'});
      preview.fade.onfinish = () => clearFade(preview);
    }
    preview.displayed = input;
    if (preview.hoverActive) indicateColor(input);
  };
  // Hover only cycles the image being explored; the global selection stays put.
  const stopHoverTimer = preview => {
    clearTimeout(preview.hoverTimer);
    preview.hoverTimer = null;
    preview.hoverRevision++;
  };
  const canHoverPlay = preview => preview.hoverActive && !pending && !dragging && !focused && !motion.matches && visible && !document.hidden;
  const refreshHover = preview => {
    stopHoverTimer(preview);
    if (!canHoverPlay(preview)) return;
    const request = preview.hoverRevision;
    preview.hoverTimer = setTimeout(async () => {
      preview.hoverTimer = null;
      const shown = preview.displayed || current;
      const input = choices[(choices.indexOf(shown) + 1) % choices.length];
      try {
        await decode(preview.card.dataset[input.value + 'Image']);
        if (request !== preview.hoverRevision || !canHoverPlay(preview)) return;
        showColor(preview, input);
        refreshHover(preview);
      } catch (_) {
        // Keep this ring's last good image and matching link on a failed read.
      }
    }, 3000);
  };
  const setPending = value => {
    pending = value;
    track.setAttribute('aria-busy', String(value));
    previews.forEach(({links}) => links.forEach(link => {
      if (value) link.setAttribute('aria-disabled', 'true');
      else link.removeAttribute('aria-disabled');
    }));
  };
  previews.forEach(({links}) => links.forEach(link => {
    ['click','auxclick'].forEach(eventName => link.addEventListener(eventName, event => {
      if (pending) event.preventDefault();
    }));
  }));
  const canPlay = () => !userPaused && !dragging && !motion.matches && visible && !document.hidden && !hovering && !focused;
  const refresh = () => {
    clearTimeout(timer);
    timer = null;
    const running = canPlay();
    section.dataset.previewPlaying = String(running);
    refreshSlides();
    previews.forEach(refreshHover);
    if (running) timer = setTimeout(async () => {
      timer = null;
      if (!canPlay() || pending) { refresh(); return; }
      const input = choices[(choices.indexOf(current) + 1) % choices.length];
      input.checked = true;
      await select(input, true);
      refresh();
    }, cycleMs);
  };
  const pause = () => {
    userPaused = true;
    refresh();
  };
  const select = async (input, automatic = false) => {
    if (!input.checked) return;
    const request = ++revision;
    if (input === current) {
      setPending(false);
      if (!automatic) status.textContent = input.dataset.metalLabel + ' selected for all eight styles.';
      return;
    }
    setPending(true);
    if (!automatic) status.textContent = 'Loading ' + input.dataset.metalLabel + ' for all eight styles.';
    try {
      await Promise.all(previews.map(({card}) => decode(card.dataset[input.value + 'Image'])));
      if (request !== revision) return;
      if (automatic && !canPlay()) {
        choices.forEach(choice => { choice.checked = choice === current; });
        return;
      }
      previews.forEach(preview => showColor(preview, input));
      current = input;
      choices.forEach(choice => { choice.checked = choice === current; });
      label.textContent = input.dataset.metalLabel;
      if (!automatic) status.textContent = input.dataset.metalLabel + ' selected for all eight styles.';
    } catch (_) {
      if (request !== revision) return;
      choices.forEach(choice => { choice.checked = choice === current; });
      userPaused = true;
      status.textContent = 'That color could not load. All eight styles remain in ' + current.dataset.metalLabel + '. Please try again.';
      refresh();
    } finally {
      if (request === revision) { setPending(false); previews.forEach(refreshHover); }
    }
  };
  choices.forEach(input => input.addEventListener('change', () => {
    pause();
    select(input);
  }));
  // Preserve native radio keyboard controls and support Home/End.
  selector.addEventListener('keydown', event => {
    if (!choices.includes(event.target) || !['Home','End'].includes(event.key)) return;
    event.preventDefault();
    pause();
    const input = choices[event.key === 'Home' ? 0 : choices.length - 1];
    input.checked = true;
    input.focus();
    select(input);
  });
  previews.forEach(preview => {
    const imageLink = preview.card.querySelector('.ring-builder-image-link');
    imageLink.addEventListener('pointerenter', event => {
      if (event.pointerType !== 'mouse') return;
      preview.hoverActive = true;
      indicateColor(preview.displayed || current);
      refreshHover(preview);
    });
    imageLink.addEventListener('pointerleave', () => {
      preview.hoverActive = false;
      stopHoverTimer(preview);
      if (preview.displayed && preview.displayed !== current) showColor(preview, current, false);
      indicateColor(current);
    });
  });
  // Freeze the shared preview while hovering; only the targeted ring cycles.
  // Keyboard focus and held pointers still freeze destinations for activation.
  track.addEventListener('pointerenter', event => {
    if (event.pointerType === 'mouse') { hovering = true; refresh(); }
  });
  track.addEventListener('pointerleave', () => { hovering = false; refresh(); });
  track.addEventListener('focusin', () => { focused = true; refresh(); });
  track.addEventListener('focusout', event => {
    if (!track.contains(event.relatedTarget)) { focused = false; refresh(); }
  });
  // Pause only while a pointer/touch is held; resume normal paging after release.
  const hold = () => { dragging = true; refresh(); };
  const release = () => { if (dragging) { dragging = false; refresh(); } };
  section.addEventListener('pointerdown', hold, {passive:true});
  section.addEventListener('touchstart', hold, {passive:true});
  ['pointerup','pointercancel','touchend','touchcancel','blur'].forEach(type => {
    window.addEventListener(type, release, {passive:true});
  });
  selector.hidden = false;
  const motionChange = () => {
    if (motion.matches) previews.forEach(clearFade);
    refresh();
  };
  if (motion.addEventListener) motion.addEventListener('change', motionChange);
  document.addEventListener('visibilitychange', refresh);
  window.addEventListener('pagehide', () => { visible = false; refresh(); });
  window.addEventListener('pageshow', event => {
    if (!event.persisted) return;
    const rect = track.getBoundingClientRect();
    visible = rect.bottom > 0 && rect.top < window.innerHeight;
    refresh();
  });
  if ('IntersectionObserver' in window) {
    new IntersectionObserver(entries => {
      visible = entries[0].isIntersecting;
      refresh();
    }, {threshold:0.15}).observe(track);
  } else { visible = true; }
  const bounds = () => {
    const end = Math.max(0, track.scrollWidth - track.clientWidth);
    controls.hidden = end <= 2;
    track.tabIndex = end > 2 ? 0 : -1;
    prev.disabled = track.scrollLeft <= 2;
    next.disabled = track.scrollLeft >= end - 2;
  };
  const step = () => cards[0].getBoundingClientRect().width + 10;
  const go = target => track.scrollTo({left:target, behavior:motion.matches?'auto':'smooth'});
  prev.addEventListener('click', () => go(track.scrollLeft - step()));
  next.addEventListener('click', () => go(track.scrollLeft + step()));
  track.addEventListener('keydown', event => {
    if (event.target !== track || track.scrollWidth <= track.clientWidth + 2) return;
    const positions = {ArrowLeft:track.scrollLeft-step(),ArrowRight:track.scrollLeft+step(),Home:0,End:track.scrollWidth};
    if (!(event.key in positions)) return;
    event.preventDefault();
    go(positions[event.key]);
  });
  track.addEventListener('scroll', bounds, {passive:true});
  let trackWidth = track.clientWidth;
  const resize = () => {
    if (trackWidth !== track.clientWidth) {
      trackWidth = track.clientWidth;
      track.scrollTo({left:mobile.matches ? slideIndex * step() : 0, behavior:'auto'});
    }
    bounds();
    refreshSlides();
  };
  track.addEventListener('scroll', () => { slideIndex = Math.round(track.scrollLeft / step()); }, {passive:true});
  window.addEventListener('resize', resize);
  if (mobile.addEventListener) mobile.addEventListener('change', resize);
  if ('ResizeObserver' in window) new ResizeObserver(resize).observe(track);
  bounds();
  refresh();
})();

